import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useFormik, yupToFormErrors, validateYupSchema } from 'formik';
import cn from 'classnames';

import { CSSObjectWithLabel } from 'react-select';

import ModalFormDatepicker from '@/components/business/ModalForm/ModalFormDatepicker/ModalFormDatepicker';
import ModalFormDropdown from '@/components/business/ModalForm/ModalFormDropdown/ModalFormDropdown';
import ModalFormTextarea from '@/components/business/ModalForm/ModalFormTextarea/ModalFormTextarea';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import TopLabel from '@/components/common/TopLabel/TopLabel';
import { DEFAULT_DATE_PATTERN } from '@/config/common.config';
import { addDays, getDateWithProperTimeOffset, isValidDate } from '@/utils/date.utils';
import { DnrContractLinesFormFields, validationSchema } from './validationSchema';
import { isDnrDateOutOfRenewalRange } from './utils';
import { DNR_TYPE } from '@/config/reasonTypes.config';
import Toggle from '@/components/common/Toggle/Toggle';
import { DnrContractLinesDto } from '../hooks/useDnrContractLines';
import ReadonlyField from '@/components/common/ModalWindow/ReadonlyField/ReadonlyField';
import LinesCount from '@/components/common/ModalWindow/LinesCount/LinesCount';

import ActionButtonsWrapper from '@/components/common/ModalWindow/ActionButtonsWrapper/ActionButtonsWrapper';

interface IDnrContractReasonModalProps {
  contract: Contract | undefined;
  onClose: () => void;
  dnrContractLines: (params: DnrContractLinesDto) => void;
  selectedContractLinesCount: number;
  reasonCodes: ReasonCode[];
  reasonTypes: ReasonType[];
  isLoadingReasonCodes?: boolean;
  lastBillingEndDate?: string | null;
  readOnly?: boolean;
}

export default function DnrContractReasonModal({
  contract,
  dnrContractLines,
  onClose,
  selectedContractLinesCount,
  reasonCodes,
  reasonTypes,
  isLoadingReasonCodes,
  lastBillingEndDate,
  readOnly
}: IDnrContractReasonModalProps) {
  const [isConfirmWindowVisible, setIsConfirmWindowVisible] = useState<boolean>(false);
  const [isDnrWarningWindowVisible, setIsDnrWarningWindowVisible] = useState<boolean>(false);
  const [isBillingEndDateWarningVisible, setIsBillingEndDateWarningVisible] = useState<boolean>(false);

  const initialValues = useMemo(() => ({
    reasonCode: '',
    comment: '',
    dnrDate: getDateWithProperTimeOffset(
      contract?.contractEndDate ? addDays(new Date(contract?.contractEndDate), 1) : new Date()
    ),
    noFurtherBilling: false,
    noFurtherBillingDate: undefined,
  }), [contract]);

  const validate = async (formValues: DnrContractLinesFormFields) => {
    const context = {
      contractEndDate: contract?.contractEndDate,
      contractRenewableTerm: contract?.renewableTerm
    };

    try {
      await validateYupSchema(formValues, validationSchema, true, context);
    }
    catch (err) {
      return yupToFormErrors(err);
    }

    return {};
  };

  const { values, setFieldValue, errors, isValid, setFieldTouched, touched } = useFormik<DnrContractLinesFormFields>({
    initialValues,
    validationSchema,
    validate,
    onSubmit: () => undefined,
    validateOnMount: true,
  });

  const confirmDNR = () => {
    if (lastBillingEndDate && values.dnrDate && dayjs(values.dnrDate).isBefore(dayjs(lastBillingEndDate))) {
      setIsBillingEndDateWarningVisible(true);
      return;
    }

    if (isDnrDateOutOfRenewalRange(values.dnrDate, contract?.contractEndDate)) {
      setIsDnrWarningWindowVisible(true);
    } else {
      setIsConfirmWindowVisible(true);
    }
  };

  const onConfirmBillingEndDateWarning = () => {
    setIsBillingEndDateWarningVisible(false);
    if (isDnrDateOutOfRenewalRange(values.dnrDate, contract?.contractEndDate)) {
      setIsDnrWarningWindowVisible(true);
    } else {
      setIsConfirmWindowVisible(true);
    }
  };

  const onCloseDnrWarningWindow = () => {
    setIsDnrWarningWindowVisible(false);
  };

  const onConfirm = () => {
    dnrContractLines(values);
    onClose();
  };

  return (
    <>
      {isConfirmWindowVisible && (
        <AgConfirmationModal
          isOpen={true}
          title="You are about to DNR Contract/Contract Line"
          onClose={onClose}
          onConfirm={onConfirm}
          contentStyle={{ width: 600 }}
        >
          {/* eslint-disable-next-line max-len */}
          Setting DNR status means stopping all billing activities related to this Contract/ Contract Line. Use it only with extra precaution.
        </AgConfirmationModal>
      )}

      {isDnrWarningWindowVisible && (
        <AgConfirmationModal
          isOpen={true}
          title="You are about to set DNR date that is out of Contract Renewal window"
          onClose={onCloseDnrWarningWindow}
          onConfirm={onConfirm}
          contentStyle={{ width: 680 }}
        >
          The DNR Date set for the Contract/Contract Lines are out of range of Contract Renewal.
          <br />
          Please check the DNR Date and confirm the selection.
        </AgConfirmationModal>
      )}

      {isBillingEndDateWarningVisible && (
        <AgConfirmationModal
          isOpen={true}
          title="DNR date is before the last billing end date"
          onClose={() => setIsBillingEndDateWarningVisible(false)}
          onConfirm={onConfirmBillingEndDateWarning}
          contentStyle={{ width: 680 }}
        >
          DNR date is before the last billing end date. Please review and make a credit note request.
        </AgConfirmationModal>
      )}

      <AgModalWindow
        title="DNR Reason"
        isOpen={true}
        onClose={onClose}
        contentStyle={{ width: 340 }}
      >
        {readOnly && (
          <div
            style={{
              padding: '10px 15px',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              fontSize: '13px',
              color: '#856404',
              marginBottom: '15px'
            }}
          >
            <strong>Note: </strong>Only users with Administrator or Full Access roles can
            edit information.
          </div>
        )}

        <LinesCount className="mb-10">
          {`${selectedContractLinesCount} Lines Selected`}
        </LinesCount>
        <div className="mb-15">
          <ModalFormDropdown<ReasonType>
            id="dnrReasonType"
            labelTitle="Reason Type"
            optionsData={reasonTypes}
            getOptionLabel={(option) => option.formattedType}
            getOptionValue={(option) => option.type}
            disabled
            defaultValue={reasonTypes.find((el) => el.type === DNR_TYPE)}
          />
        </div>
        <div className="mb-15">
          <ModalFormDropdown<ReasonCode>
            id="dnrReason"
            labelTitle="Reason Name"
            requiredIcon={true}
            isSearchable={true}
            optionsData={reasonCodes}
            placeholder="Select or type to search"
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.code}
            selectedOption={reasonCodes.find((el) => el.code === values.reasonCode) ?? null}
            customStyles={{
              menuList: (provided) => ({ ...provided, maxHeight: 200 } as CSSObjectWithLabel)
            }}
            menuPortalTarget={document.querySelector('body')}
            handleChange={(newValue) => {
              setFieldTouched('reasonCode', true, false);
              setFieldValue('reasonCode', newValue?.code);
            }}
            validationMessage={
              (errors?.reasonCode && touched.reasonCode) ? errors.reasonCode : undefined
            }
            isLoading={isLoadingReasonCodes}
            disabled={isLoadingReasonCodes}
          />
        </div>

        <div className="mb-15">
          <ModalFormDatepicker
            id="dnrDate"
            labelTitle="DNR Date"
            requiredIcon={true}
            onChange={({ value }) => {
              setFieldTouched('dnrDate', true, false);
              setFieldValue('dnrDate', value);
            }}
            value={isValidDate(values.dnrDate) ? new Date(values.dnrDate) : undefined}
            validationMessage={
              (errors?.dnrDate && touched.dnrDate) ? errors.dnrDate as string : undefined
            }
          />
        </div>

        <div className="mb-15">
          <ModalFormTextarea
            id="dnrComment"
            labelTitle="Comment"
            requiredIcon={true}
            placeholder="Enter short comments"
            onChange={(event) => {
              setFieldTouched('comment', true, false);
              setFieldValue('comment', event.target.value);
            }}
            validationMessage={
              (errors?.comment && touched.comment) ? errors.comment : undefined
            }
          />
        </div>

        <div className="mb-15">
          <TopLabel label="Contract End Date" />
          <ReadonlyField>
            {contract?.contractEndDate
              ? dayjs(contract.contractEndDate).format(DEFAULT_DATE_PATTERN)
              : 'Not specified'}
          </ReadonlyField>
        </div>

        <div className="mb-15">
          <TopLabel label="Contract Term (Months)" />
          <ReadonlyField>{contract?.term}</ReadonlyField>
        </div>

        <div
          className={cn(
            'd-flex',
            'align-items-center',
            { 'mb-15': values.noFurtherBilling }
          )}
        >
          <div className="mr-10">
            <Toggle
              id="no-further-billing"
              isEnable={values.noFurtherBilling}
              onChange={() => {
                const newValue = !values.noFurtherBilling;
                setFieldTouched('noFurtherBilling', true, false);
                setFieldValue('noFurtherBilling', newValue);

                if (newValue === false) {
                  setFieldValue('noFurtherBillingDate', undefined);
                }
                else {
                  setFieldValue('noFurtherBillingDate', getDateWithProperTimeOffset(new Date()));
                }
              }}
            />
          </div>

          <TopLabel id="no-further-billing" label="No Further Billing" withMargin={false} />
        </div>

        {values.noFurtherBilling && (
          <div>
            <ModalFormDatepicker
              id="noFurtherBillingDate"
              labelTitle="No Further Billing Date"
              onChange={({ value }) => {
                setFieldTouched('noFurtherBillingDate', true, false);
                setFieldValue('noFurtherBillingDate', value);
              }}
              value={values.noFurtherBillingDate}
              validationMessage={
                (errors?.noFurtherBillingDate && touched.noFurtherBillingDate) ? errors.noFurtherBillingDate : undefined
              }
            />
          </div>
        )}

        <ActionButtonsWrapper
          leftButton={(
            <Button
              buttonStyleType={ButtonStyleType.SECONDARY}
              onClick={onClose}
            >
              Cancel
            </Button>
          )}
          rightButton={(
            <Button
              disabled={!isValid || readOnly}
              buttonStyleType={ButtonStyleType.PRIMARY}
              onClick={confirmDNR}
            >
              Save
            </Button>
          )}
        />
      </AgModalWindow>
    </>
  );
}
